import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import changelogEn from '../../../../CHANGELOG.en.md?raw'
import changelogDe from '../../../../CHANGELOG.md?raw'

/**
 * Minimal, dependency-free Markdown renderer for the CHANGELOG.
 *
 * Intentionally supports only the subset of Markdown used in
 * `CHANGELOG.md`: `#`/`##`/`###` headings, `-` bullet lists (with wrapped
 * continuation lines), paragraphs, inline `code` and `**bold**`. This keeps
 * us from pulling in a full markdown dependency for a single read-only view.
 */

/** German UI languages get the German changelog; everything else falls back to English. */
const GERMAN_LANGUAGES = new Set(['de', 'at', 'ch'])

/** Pick the changelog markdown matching the current UI language. */
export function useChangelogSource(): string {
  const { i18n } = useTranslation()
  const uiLanguage = (i18n.language || 'en').split('-')[0]?.toLowerCase() ?? 'en'
  return GERMAN_LANGUAGES.has(uiLanguage) ? changelogDe : changelogEn
}

/** Render inline `code` and `**bold**` spans inside a single line of text. */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const regex = /(`[^`]+`|\*\*[^*]+\*\*)/g
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex iteration
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    const token = match[0]
    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key++}
          className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground"
        >
          {token.slice(1, -1)}
        </code>
      )
    } else {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>)
    }
    lastIndex = regex.lastIndex
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }
  return nodes
}

/** Render a CHANGELOG markdown string into React blocks. */
export function ChangelogMarkdown({ source }: { source: string }) {
  const lines = source.split('\n')
  const blocks: ReactNode[] = []
  let listItems: string[] | null = null
  let paragraph: string[] | null = null
  let key = 0

  const flushList = () => {
    if (listItems) {
      const items = listItems
      blocks.push(
        <ul key={key++} className="my-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {items.map((item, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static, non-reordered list
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      )
      listItems = null
    }
  }

  const flushParagraph = () => {
    if (paragraph) {
      const text = paragraph.join(' ')
      blocks.push(
        <p key={key++} className="my-2 text-sm text-muted-foreground">
          {renderInline(text)}
        </p>
      )
      paragraph = null
    }
  }

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '')
    if (!line.trim()) {
      flushList()
      flushParagraph()
      continue
    }
    if (line.startsWith('### ')) {
      flushList()
      flushParagraph()
      blocks.push(
        <h4 key={key++} className="mt-4 mb-1 font-semibold text-foreground text-sm">
          {renderInline(line.slice(4))}
        </h4>
      )
      continue
    }
    if (line.startsWith('## ')) {
      flushList()
      flushParagraph()
      blocks.push(
        <h3
          key={key++}
          className="mt-6 mb-2 border-border border-t pt-4 font-semibold text-base text-foreground first:mt-0 first:border-t-0 first:pt-0"
        >
          {renderInline(line.slice(3))}
        </h3>
      )
      continue
    }
    if (line.startsWith('# ')) {
      // Top-level "# Changelog" title is shown via the card header instead.
      flushList()
      flushParagraph()
      continue
    }
    const listMatch = line.match(/^(\s*)-\s+(.*)$/)
    if (listMatch) {
      flushParagraph()
      if (!listItems) listItems = []
      listItems.push(listMatch[2] ?? '')
      continue
    }
    // Wrapped continuation line: append to the current list item or paragraph.
    if (listItems && listItems.length > 0) {
      const lastIdx = listItems.length - 1
      listItems[lastIdx] = `${listItems[lastIdx] ?? ''} ${line.trim()}`
      continue
    }
    if (!paragraph) paragraph = []
    paragraph.push(line.trim())
  }

  flushList()
  flushParagraph()

  return <>{blocks}</>
}
